import React from 'react';
import { map, intersectionWith } from 'lodash';

import {
  NChanEventModel,
  NotifyDetailsUserModel,
  NotifyDetailsWorkerModel,
  NotifyDetailsPartnerModel,
  BackofficeNotificationType,
  NotifyDetailsCustomerModel,
  NotifyDetailsAssignmentModel,
  BackofficeNotificationStreamModel,
  BackofficeInstantNotificationsResponseModel,
} from 'api/backofficeNotificationApiClient';
import { BOUserNotificationModel } from 'api/adminApiClient';
import {
  APPROVED_ENTITY_CONTAINER_ID,
  POPUP_NOTIFICATIONS_CONTAINER_ID,
} from './toastUtils';
import BaseToast from 'components/wrappers/BaseToast';
import { AssignmentInfoTypes } from 'features/assignment/enums';
import { CompanyProfileSection } from 'features/account/enums';
import { AdminNotificationType } from 'components/modals/SendNotificationModal/enums';

export const getAdminNotificationTypeLabel = (
  notificationType: AdminNotificationType,
) => {
  switch (notificationType) {
    case AdminNotificationType.InAppNotification:
      return 'In-app notification';

    case AdminNotificationType.EmailNotification:
      return 'Email notification';

    case AdminNotificationType.Both:
      return 'Both';

    default:
      return '';
  }
};

export const getAdminNotificationOptions = () => [
  {
    label: getAdminNotificationTypeLabel(
      AdminNotificationType.InAppNotification,
    ),
    value: AdminNotificationType.InAppNotification,
  },
  {
    label: getAdminNotificationTypeLabel(
      AdminNotificationType.EmailNotification,
    ),
    value: AdminNotificationType.EmailNotification,
  },
  {
    label: getAdminNotificationTypeLabel(AdminNotificationType.Both),
    value: AdminNotificationType.Both,
  },
];

interface MapRecipientsModel {
  selectedUserIds: string[];
  allPossibleRecipients: BOUserNotificationModel[];
  notificationType: AdminNotificationType;
  isAllSelected: boolean;
}
export const mapRecipients = ({
  selectedUserIds,
  allPossibleRecipients,
  notificationType,
  isAllSelected,
}: MapRecipientsModel) => {
  switch (notificationType) {
    case AdminNotificationType.InAppNotification:
      return isAllSelected
        ? map(allPossibleRecipients, (recipient) => recipient.userId as string)
        : selectedUserIds;

    case AdminNotificationType.EmailNotification:
      const emailRecipients = isAllSelected
        ? allPossibleRecipients
        : intersectionWith(
            allPossibleRecipients,
            selectedUserIds,
            (recipient, userId) => recipient.userId === userId,
          );
      return map(emailRecipients, (recipient) => recipient.email as string);

    default:
      return [];
  }
};

export const getNotificationIcon = (
  notificationType: BackofficeNotificationType,
) => {
  switch (notificationType) {
    case BackofficeNotificationType.NewAssignmentRequest:
    case BackofficeNotificationType.RenewWithChangesNotify:
    case BackofficeNotificationType.RenewWithoutChangesNotify:
    case BackofficeNotificationType.ApprovedAssignmentNotify:
    case BackofficeNotificationType.CompletedStageAssignmentNotify:
    case BackofficeNotificationType.KronosAssigmentIntegrationErrorNotify:
    case BackofficeNotificationType.AssignmentUpdatedNotify:
    case BackofficeNotificationType.AssignmentEffectiveDateNotify:
    case BackofficeNotificationType.CaliforniaWorkerTerminationNotify:
      return <span className="icon icon-assignmant assignment-icon" />;

    case BackofficeNotificationType.NewCustomerRequest:
    case BackofficeNotificationType.ApprovedCustomerNotify:
    case BackofficeNotificationType.CompletedStageCustomerNotify:
      return <span className="icon icon-client new-client-icon success" />;

    case BackofficeNotificationType.CustomerRequestAccepted:
      return <span className="icon icon-usergroup-add success" />;

    case BackofficeNotificationType.CustomerRequestDeclined:
      return <span className="icon icon-usergroup-add error" />;

    case BackofficeNotificationType.AssignmentRequestAccepted:
      return <span className="icon icon-usergroup-add success" />;

    case BackofficeNotificationType.AssignmentRequestDeclined:
      return <span className="icon icon-usergroup-add error" />;

    case BackofficeNotificationType.PricingAgreementConfirmed:
      return <span className="icon icon-dollar" />;

    case BackofficeNotificationType.NewOutreachDate:
      return <span className="icon icon-dollar error" />;

    case BackofficeNotificationType.PricingAgreementReceived:
      return <span className="icon icon-dollar success" />;

    case BackofficeNotificationType.PricingAgreementChangedRequest:
      return <span className="icon icon-dollar success" />;

    case BackofficeNotificationType.BroadcastMessage:
      return <span className="icon icon-letter" />;

    case BackofficeNotificationType.AssignmentEndDaysNotify:
      return <span className="icon icon-error error" />;

    case BackofficeNotificationType.NewPartnerWithoutMPProfile:
      return <span className="icon icon-man success"></span>;

    case BackofficeNotificationType.QuestionnaireNotify:
    case BackofficeNotificationType.ContractorQuestionnaireNotify:
      return <span className="icon icon icon-people success"></span>;

    case BackofficeNotificationType.SoftStopMemberNotify:
    case BackofficeNotificationType.SoftStopCustomerNotify:
    case BackofficeNotificationType.SoftStopAssignmentNotify:
      return <span className="icon icon-attention-filled warning" />;

    case BackofficeNotificationType.HardStopMemberNotify:
    case BackofficeNotificationType.HardStopCustomerNotify:
    case BackofficeNotificationType.HardStopAssignmentNotify:
      return <span className="icon icon-closed-filled error" />;

    case BackofficeNotificationType.CompletedStageMemberNotify:
      return <span className="icon icon-user-settings" />;

    case BackofficeNotificationType.NewWorkerDuplicateCreatedNotify:
      return <span className="icon icon-worker error" />;

    case BackofficeNotificationType.MemberRequestedInformationProvidedNotify:
    case BackofficeNotificationType.CustomerRequestedInformationProvidedNotify:
    case BackofficeNotificationType.AssignmentRequestedInformationProvidedNotify:
      return <span className="icon icon-request" />;

    case BackofficeNotificationType.CustomerUpdatedNotify:
      return <span className="icon icon-edit-customer" />;

    case BackofficeNotificationType.WorkerUpdatedNotify:
      return <span className="icon icon-edit-worker" />;

    default:
      return null;
  }
};

export const checkIsErrorNotification = (
  notificationType: BackofficeNotificationType,
) => {
  switch (notificationType) {
    case BackofficeNotificationType.KronosAssigmentIntegrationErrorNotify:
      return true;
    default:
      return false;
  }
};

export const getNotificationRedirectURL = (
  notification: BackofficeNotificationStreamModel,
) => {
  const { type, details } = notification;
  let url = '';

  switch (type) {
    case BackofficeNotificationType.CustomerRequestAccepted:
    case BackofficeNotificationType.CustomerRequestDeclined:
    case BackofficeNotificationType.NewCustomerRequest:
    case BackofficeNotificationType.HardStopCustomerNotify:
    case BackofficeNotificationType.SoftStopCustomerNotify:
    case BackofficeNotificationType.CompletedStageCustomerNotify:
    case BackofficeNotificationType.ApprovedCustomerNotify:
    case BackofficeNotificationType.CustomerRequestedInformationProvidedNotify:
    case BackofficeNotificationType.CustomerUpdatedNotify:
      const { customerKey, clientId } = details as NotifyDetailsCustomerModel;

      url = `/customer/${customerKey || clientId}`;
      break;

    case BackofficeNotificationType.NewAssignmentRequest:
    case BackofficeNotificationType.RenewWithChangesNotify:
    case BackofficeNotificationType.RenewWithoutChangesNotify:
    case BackofficeNotificationType.AssignmentRequestAccepted:
    case BackofficeNotificationType.AssignmentRequestDeclined:
    case BackofficeNotificationType.AssignmentEndDaysNotify:
    case BackofficeNotificationType.QuestionnaireNotify:
    case BackofficeNotificationType.ContractorQuestionnaireNotify:
    case BackofficeNotificationType.HardStopAssignmentNotify:
    case BackofficeNotificationType.SoftStopAssignmentNotify:
    case BackofficeNotificationType.CompletedStageAssignmentNotify:
    case BackofficeNotificationType.ApprovedAssignmentNotify:
    case BackofficeNotificationType.AssignmentRequestedInformationProvidedNotify:
    case BackofficeNotificationType.KronosAssigmentIntegrationErrorNotify:
    case BackofficeNotificationType.AssignmentUpdatedNotify:
    case BackofficeNotificationType.AssignmentEffectiveDateNotify:
    case BackofficeNotificationType.CaliforniaWorkerTerminationNotify:
      const { assignmentKey, assignmentId } =
        details as NotifyDetailsAssignmentModel;

      url = `/assignment/${assignmentKey || assignmentId}?section=${
        AssignmentInfoTypes.Primary
      }`;
      break;

    case BackofficeNotificationType.PricingAgreementConfirmed:
    case BackofficeNotificationType.NewOutreachDate:
    case BackofficeNotificationType.PricingAgreementChangedRequest:
    case BackofficeNotificationType.MemberRequestedInformationProvidedNotify:
      const { memberKey, userId } = details as NotifyDetailsUserModel;

      url = `/member/${memberKey || userId}`;
      break;

    case BackofficeNotificationType.PricingAgreementReceived:
    case BackofficeNotificationType.SoftStopMemberNotify:
    case BackofficeNotificationType.HardStopMemberNotify:
    case BackofficeNotificationType.CompletedStageMemberNotify:
      url = `/company-profile?section=${CompanyProfileSection.PrimaryInfo}`;
      break;

    case BackofficeNotificationType.NewPartnerWithoutMPProfile:
      const { partnerId } = details as NotifyDetailsPartnerModel;

      url = `/partner/${partnerId}`;
      break;

    case BackofficeNotificationType.NewWorkerDuplicateCreatedNotify:
    case BackofficeNotificationType.WorkerUpdatedNotify:
      const { workerKey, workerId } = details as NotifyDetailsWorkerModel;

      url = `/worker/${workerKey || workerId}`;
      break;

    default:
      break;
  }

  return url;
};

export const getParsedNotification = (message: string) => {
  let notification: NChanEventModel;

  try {
    notification = JSON.parse(message);
  } catch (e) {
    notification = {};
  }

  return notification;
};

export const getApprovedEntitiesNotificationIds = (
  approvedEntities: BackofficeInstantNotificationsResponseModel,
) => {
  const notifications = [
    ...(approvedEntities.assignmentNotifications || []),
    ...(approvedEntities.customerNotifications || []),
  ];
  const notificationIds = notifications.map(
    (notification) => notification.notificationId,
  );

  return notificationIds;
};

export const clearPopupNotificationsWaitingQueue = () => {
  BaseToast.clearWaitingQueue({
    containerId: POPUP_NOTIFICATIONS_CONTAINER_ID,
  });
};

export const closeAllPopupNotifications = () => {
  BaseToast.dismissAll(POPUP_NOTIFICATIONS_CONTAINER_ID);
};

export const closeAllApprovedEntityNotifications = () => {
  BaseToast.dismissAll(APPROVED_ENTITY_CONTAINER_ID);
};
